import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ApiService } from '@core/api/angular';
import { getVicnamesRadiusFromCoordinates } from '@core/api/angular/operations/get-vicnames-radius-from-coordinates';
import { normalSearchVicnamesFeaturesOptions } from '@core/api/angular/operations/normal-search-vicnames-features-options';
import { MAP_ROOT } from '@lassi/config';
import { icon, LatLng, marker, Marker, Polygon } from 'leaflet';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs';
import { MapService, SelectMode } from './map';
import { VicnamesPolygonSearchMode } from './map/map-mode/draw.mode';
import { IVicnamesRoadDuplicateSearchForm } from './models';
import { IVicnamesFeatureDuplicateSearchForm } from './models/vicnames-feature.model';
import { DMSPipe } from './pipes/coordinates.pipe';
import { RouteUtilsService } from './route-utils.service';
import { ToastService } from './toast/toast.service';

export enum VicnamesSearchType {
  FEATURE_SEARCH = 'features',
  ROAD_SEARCH = 'roads',
}

@Injectable({
  providedIn: 'root',
})
export class VicnamesSearchService {
  private readonly mapService = inject(MapService);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mapRoot = inject(MAP_ROOT);
  private currentMarker: Marker | null = null;

  readonly POLYGON_SEARCH_ALLOWED_THRESHOLD = 10_000;

  getFeatureSearchOptions() {
    return this.apiService.invoke(normalSearchVicnamesFeaturesOptions);
  }

  public startGetCoordinateFromMap(
    formValues:
      | IVicnamesFeatureDuplicateSearchForm
      | IVicnamesRoadDuplicateSearchForm,
    searchType: VicnamesSearchType,
  ) {
    const mapMode = this.mapService.switchMapMode(SelectMode, {
      stopTooltip: 'Stop selecting coordinate',
    });

    mapMode.mapClick$
      .pipe(
        map(({ event }) => event),
        filter((e) => e.originalEvent.target === e.target.getContainer()),
        take(1),
        tap((e) => {
          this.addOrReplaceCoordinatesMarker(e.latlng);
        }),
        switchMap((e) => {
          const dmsPipe = new DMSPipe();

          // convert negative lat to positive because we only target australia
          const latitude = dmsPipe.transform(
            Math.abs(e.latlng.lat).toString().slice(0, 16),
          );
          const longitude = dmsPipe.transform(
            e.latlng.lng.toString().slice(0, 16),
          );
          return this.apiService
            .invoke(getVicnamesRadiusFromCoordinates, {
              latitude,
              longitude,
            })
            .pipe(
              switchMap((data) =>
                this.goBackToCoordinateSearchForm(
                  {
                    ...formValues,
                    latitude,
                    longitude,
                    radius: data.radius,
                  },
                  searchType,
                ),
              ),
              catchError((error) => {
                console.error(error);
                this.toast.showErrorMessage(
                  'Could not get the radius for this location',
                );
                return this.goBackToCoordinateSearchForm(
                  formValues,
                  searchType,
                );
              }),
            );
        }),
        finalize(() => this.mapService.resetMapMode()),
      )
      .subscribe();
  }

  private async goBackToCoordinateSearchForm(
    formValues:
      | IVicnamesFeatureDuplicateSearchForm
      | IVicnamesRoadDuplicateSearchForm,
    searchType: VicnamesSearchType,
  ) {
    if (searchType === VicnamesSearchType.FEATURE_SEARCH) {
      return await this.router.navigate(
        [
          this.mapRoot,
          {
            outlets: {
              [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
                RouteUtilsService.MAP_MENU_ROOT_PATH,
                'vicnames',
                'feature-search',
                'duplicates',
              ],
            },
          },
        ],
        {
          queryParamsHandling: 'merge',
          queryParams: {
            duplicateFeatureSearchForm: JSON.stringify(formValues),
          },
        },
      );
    } else if (searchType === VicnamesSearchType.ROAD_SEARCH) {
      return await this.router.navigate(
        [
          this.mapRoot,
          {
            outlets: {
              [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
                RouteUtilsService.MAP_MENU_ROOT_PATH,
                'vicnames',
                'road-search',
                'duplicates',
              ],
            },
          },
        ],
        {
          queryParamsHandling: 'merge',
          queryParams: {
            duplicateRoadSearchForm: JSON.stringify(formValues),
          },
        },
      );
    } else {
      return Promise.reject(new Error('Invalid search type'));
    }
  }

  private addOrReplaceCoordinatesMarker(latlng: LatLng): void {
    this.removeCoordinatesMarker();
    this.currentMarker = marker(latlng, {
      icon: icon({
        iconAnchor: [25 / 2, 41],
        iconUrl: '/media/marker-icon.png',
        shadowUrl: '/media/marker-shadow.png',
      }),
      interactive: false,
    });
    this.currentMarker.addTo(this.mapService.map);
  }

  removeCoordinatesMarker(): void {
    if (this.currentMarker) {
      this.mapService.map.removeLayer(this.currentMarker);
      this.currentMarker = null;
    }
  }

  private goToResults() {
    return this.router.navigate(
      [
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              'vicnames',
              'polygon-search',
              'feature',
            ],
          },
        },
      ],
      {
        queryParamsHandling: 'preserve',
      },
    );
  }

  resumePolygonSearch() {
    // Don't restore the polygon object - it's stale
    // Just navigate and let the resolver use the stored GeoJSON
    return this.goToResults();
  }

  readonly lastPolygonGeoJson = signal<string | null>(null);
  readonly polygonSearch = signal<Polygon | null>(null);
  startPolygonSearch() {
    const mapMode = this.mapService.switchMapMode(VicnamesPolygonSearchMode, {
      shape: 'Polygon',
      allowSelfIntersection: false,
    });

    const sub = this.mapService.scaleFactor$
      .pipe(
        filter((scale) => scale > this.POLYGON_SEARCH_ALLOWED_THRESHOLD),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.toast.showErrorMessage('Zoomed out too far to use polygon search');
        this.mapService.resetMapMode();
        this.polygonSearch()?.remove();
        this.polygonSearch.set(null);
      });

    mapMode.shape$
      .pipe(
        tap((e) => {
          const polygon = e.layer as Polygon;
          const geoJsonStr = JSON.stringify(polygon.toGeoJSON().geometry);

          this.polygonSearch.set(polygon);
          // Store GeoJSON immediately so it's available after polygon is removed from map
          this.lastPolygonGeoJson.set(geoJsonStr);
        }),
        switchMap(() => {
          return this.goToResults();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        complete: () => {
          this.polygonSearch()?.remove();
          this.polygonSearch.set(null);
          sub.unsubscribe();
        },
      });
  }
}
